import React, { useEffect, useState } from 'react';
import { Bell, MessageSquare, BookOpen, BarChart2, FileText, Megaphone, Filter, CheckCircle, ChevronDown } from 'lucide-react';
import AuroraFloatingCard from '../ui/AuroraFloatingCard';
import { Link } from 'react-router-dom';
// Notification types with their respective icons
const notificationIcons = {
  message: MessageSquare,
  forum: MessageSquare,
  homework: BookOpen,
  report: BarChart2,
  document: FileText,
  announcement: Megaphone,
  system: Bell
};
interface Notification {
  id: string;
  type: keyof typeof notificationIcons;
  title: string;
  message: string;
  time: string;
  read: boolean;
  link: string;
  category: string;
}
// Mock data for notifications
const mockNotifications: Notification[] = [{
  id: '1',
  type: 'message',
  title: 'Yeni Mesaj',
  message: 'Zeynep Öğretmen size yeni bir mesaj gönderdi.',
  time: '5 dakika önce',
  read: false,
  link: '/yonetici/mesajlar',
  category: 'Mesajlar'
}, {
  id: '2',
  type: 'forum',
  title: 'Forum Yanıtı',
  message: 'Mehmet K., "Öğrencilere nasıl daha etkili motivasyon sağlayabiliriz?" konusuna yanıt yazdı.',
  time: '30 dakika önce',
  read: false,
  link: '/yonetici/forum',
  category: 'Forum'
}, {
  id: '3',
  type: 'homework',
  title: 'Ödev Hatırlatması',
  message: '8-A sınıfı için "Matematik - Üslü Sayılar" ödevinin son teslim tarihi yarın.',
  time: '2 saat önce',
  read: true,
  link: '/yonetici/ogrenciler',
  category: 'Ödev ve Program'
}, {
  id: '4',
  type: 'report',
  title: 'Haftalık Rapor Hazır',
  message: '9-B sınıfı için haftalık ilerleme raporu hazır.',
  time: '1 gün önce',
  read: true,
  link: '/yonetici/raporlar',
  category: 'Raporlar'
}, {
  id: '5',
  type: 'announcement',
  title: 'Yeni Duyuru',
  message: 'Veli toplantısı tarihi 15 Mayıs Cumartesi saat 14:00 olarak belirlenmiştir.',
  time: '2 gün önce',
  read: true,
  link: '/yonetici/duyurular',
  category: 'Duyurular'
}, {
  id: '6',
  type: 'system',
  title: 'Sistem Güncellemesi',
  message: 'Platformda yeni özellikler aktif edildi. Detayları görmek için tıklayın.',
  time: '3 gün önce',
  read: true,
  link: '/yonetici/duyurular',
  category: 'Sistem'
}, {
  id: '7',
  type: 'forum',
  title: 'Konunuz Beğenildi',
  message: 'Mehmet Öğretmen, "LGS Fen Bilimleri müfredatı hakkında öğretmen görüşleri" konunuzu beğendi.',
  time: '3 gün önce',
  read: true,
  link: '/yonetici/forum',
  category: 'Forum'
}, {
  id: '8',
  type: 'message',
  title: 'Yeni Mesaj',
  message: 'Ahmet Veli size yeni bir mesaj gönderdi.',
  time: '4 gün önce',
  read: true,
  link: '/yonetici/mesajlar',
  category: 'Mesajlar'
}, {
  id: '9',
  type: 'report',
  title: 'Sınav Sonuçları',
  message: 'Deneme sınavı sonuçları açıklandı.',
  time: '5 gün önce',
  read: true,
  link: '/yonetici/raporlar',
  category: 'Raporlar'
}, {
  id: '10',
  type: 'homework',
  title: 'Yeni Ödev',
  message: '7-C sınıfı için yeni bir ödev eklendi.',
  time: '1 hafta önce',
  read: true,
  link: '/yonetici/ogrenciler',
  category: 'Ödev ve Program'
}, {
  id: '11',
  type: 'announcement',
  title: 'Tatil Duyurusu',
  message: '19 Mayıs tatili nedeniyle kurumumuz kapalı olacaktır.',
  time: '1 hafta önce',
  read: true,
  link: '/yonetici/duyurular',
  category: 'Duyurular'
}, {
  id: '12',
  type: 'system',
  title: 'Hesap Güvenliği',
  message: 'Hesabınızın şifresini son 30 gün içinde değiştirmediniz.',
  time: '2 hafta önce',
  read: true,
  link: '/yonetici/profil',
  category: 'Sistem'
}];
export function NotificationHistory() {
  const [notifications, setNotifications] = useState<Notification[]>(mockNotifications);
  const [filterType, setFilterType] = useState<'all' | 'unread'>('all');
  const [categoryFilter, setCategoryFilter] = useState('Tüm Türler');
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  // Get unique categories from notifications
  const categories = ['Tüm Türler', ...Array.from(new Set(notifications.map(n => n.category)))];
  // Filter notifications based on current filters
  const filteredNotifications = notifications.filter(notification => {
    const matchesReadFilter = filterType === 'all' || filterType === 'unread' && !notification.read;
    const matchesCategoryFilter = categoryFilter === 'Tüm Türler' || notification.category === categoryFilter;
    return matchesReadFilter && matchesCategoryFilter;
  });
  const handleMarkAllAsRead = () => {
    setNotifications(notifications.map(notification => ({
      ...notification,
      read: true
    })));
  };
  const handleMarkAsRead = (id: string) => {
    setNotifications(notifications.map(notification => notification.id === id ? {
      ...notification,
      read: true
    } : notification));
  };
  // Simulate loading more notifications when scrolling
  const loadMoreNotifications = () => {
    // In a real application, you would fetch more notifications from an API
    // For this example, we'll just simulate it
    setTimeout(() => {
      if (page < 3) {
        // Add more mock notifications
        const newNotifications = [...notifications];
        for (let i = 0; i < 5; i++) {
          const randomIndex = Math.floor(Math.random() * mockNotifications.length);
          const randomNotification = {
            ...mockNotifications[randomIndex],
            id: `new-${page}-${i}`,
            time: `${page + 2} hafta önce`
          };
          newNotifications.push(randomNotification);
        }
        setNotifications(newNotifications);
        setPage(page + 1);
      } else {
        setHasMore(false);
      }
    }, 1000);
  };
  // Handle infinite scroll
  useEffect(() => {
    const handleScroll = () => {
      if (window.innerHeight + document.documentElement.scrollTop >= document.documentElement.scrollHeight - 100 && hasMore) {
        loadMoreNotifications();
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasMore, page]);
  return <div className="space-y-6" data-id="element-795">
      <div className="flex items-center gap-3" data-id="element-796">
        <h1 className="text-3xl font-bold" data-id="element-797">Bildirim Geçmişi</h1>
        <Bell className="w-8 h-8 text-orange-400" data-id="element-798" />
      </div>
      <AuroraFloatingCard data-id="element-799">
        <div className="p-6" data-id="element-800">
          {/* Filter Controls */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6" data-id="element-801">
            <div className="flex gap-3" data-id="element-802">
              <button onClick={() => setFilterType('all')} className={`px-4 py-2 rounded-lg transition-colors ${filterType === 'all' ? 'bg-purple-500/20 text-purple-400 border border-purple-500/50' : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10'}`} data-id="element-803">
                Tümü
              </button>
              <button onClick={() => setFilterType('unread')} className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${filterType === 'unread' ? 'bg-purple-500/20 text-purple-400 border border-purple-500/50' : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10'}`} data-id="element-804">
                <span className="w-2 h-2 rounded-full bg-blue-400" data-id="element-805"></span>
                Okunmamışlar
              </button>
              <div className="relative" data-id="element-806">
                <button onClick={() => setShowCategoryDropdown(!showCategoryDropdown)} className="px-4 py-2 bg-white/5 text-gray-300 rounded-lg border border-white/10 hover:border-purple-500/50 transition-colors flex items-center gap-2" data-id="element-807">
                  <Filter className="w-4 h-4" data-id="element-808" />
                  <span data-id="element-809">{categoryFilter}</span>
                  <ChevronDown className="w-4 h-4" data-id="element-810" />
                </button>
                {showCategoryDropdown && <div className="absolute left-0 z-10 mt-1 w-56 bg-black/90 rounded-lg shadow-lg border border-gray-800 overflow-hidden" data-id="element-811">
                    {categories.map(category => <button key={category} onClick={() => {
                  setCategoryFilter(category);
                  setShowCategoryDropdown(false);
                }} className={`w-full px-4 py-2 text-left hover:bg-purple-500/20 ${categoryFilter === category ? 'bg-purple-500/20 text-purple-400' : 'text-white'}`} data-id="element-812">
                        {category}
                      </button>)}
                  </div>}
              </div>
            </div>
            {notifications.some(n => !n.read) && <button onClick={handleMarkAllAsRead} className="px-4 py-2 text-sm text-blue-400 hover:text-blue-300 flex items-center gap-2 transition-colors" data-id="element-813">
                <CheckCircle className="w-4 h-4" data-id="element-814" />
                <span data-id="element-815">Tümünü Okundu İşaretle</span>
              </button>}
          </div>
          {/* Notification List */}
          <div className="space-y-3" data-id="element-816">
            {filteredNotifications.length > 0 ? filteredNotifications.map(notification => {
            const Icon = notificationIcons[notification.type] || notificationIcons.system;
            return <Link key={notification.id} to={notification.link} className={`block p-4 rounded-lg border ${!notification.read ? 'bg-purple-500/10 border-purple-500/30' : 'bg-black/50 border-white/10 hover:border-purple-500/20'} transition-colors`} onClick={() => handleMarkAsRead(notification.id)} data-id="element-817">
                    <div className="flex gap-4" data-id="element-818">
                      <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${!notification.read ? 'bg-purple-500/20 text-purple-400' : 'bg-gray-800 text-gray-400'}`} data-id="element-819">
                        <Icon className="w-6 h-6" data-id="element-820" />
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-821">
                        <div className="flex flex-wrap justify-between items-start gap-2 mb-1" data-id="element-822">
                          <div className="flex items-center gap-2" data-id="element-823">
                            <h3 className={`font-medium ${!notification.read ? 'text-white' : 'text-gray-300'}`} data-id="element-824">
                              {notification.title}
                            </h3>
                            {!notification.read && <span className="w-2 h-2 rounded-full bg-blue-400" data-id="element-825"></span>}
                          </div>
                          <span className="text-xs text-gray-500" data-id="element-826">
                            {notification.time}
                          </span>
                        </div>
                        <p className="text-sm text-gray-400" data-id="element-827">
                          {notification.message}
                        </p>
                        <div className="mt-2 flex items-center justify-between" data-id="element-828">
                          <span className="text-xs px-2 py-1 rounded-full bg-gray-800 text-gray-400" data-id="element-829">
                            {notification.category}
                          </span>
                          <span className="text-xs text-blue-400" data-id="element-830">
                            Detayları Gör
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>;
          }) : <div className="py-12 text-center" data-id="element-831">
                <Bell className="w-16 h-16 text-gray-700 mx-auto mb-4" data-id="element-832" />
                <p className="text-gray-500 text-lg" data-id="element-833">
                  {filterType === 'unread' ? 'Okunmamış bildiriminiz bulunmuyor' : categoryFilter !== 'Tüm Türler' ? `${categoryFilter} kategorisinde bildirim bulunamadı` : 'Bildiriminiz bulunmuyor'}
                </p>
              </div>}
            {/* Loading indicator for infinite scroll */}
            {hasMore && filteredNotifications.length > 0 && <div className="py-4 text-center" data-id="element-834">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-purple-400 border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]" data-id="element-835"></div>
                <p className="mt-2 text-sm text-gray-500" data-id="element-836">
                  Daha fazla yükleniyor...
                </p>
              </div>}
          </div>
        </div>
      </AuroraFloatingCard>
    </div>;
}